/**
 * Structured audit events from Loki, served by the sig-control plane
 * (controlApi, direct, JWT bearer) at /logs, /logs/errors, /logs/rejects. Tenant
 * is IMPLICIT (resolved server-side from the JWT org claim): results are scoped to
 * the caller's OWNED streams, so no serviceId appears in the path. `serviceId` is
 * kept only to scope the react-query cache. Returns the full look-back window
 * (default 1h, override with ?since); for longer ranges query Loki / Grafana.
 */
import { useQuery } from '@tanstack/react-query';
import { controlApi } from '@/api/client';
import { aesKeys, type AesLogsResponse } from './_shared';

/**
 * Which view to read:
 *   `logz`    = all audit events
 *   `errorz`  = server errors only (every 5xx, via the http.server_error chokepoint)
 *   `rejectz` = client errors (4xx): auth rejects + forwarded upstream 4xx
 * errorz and rejectz are separate so neither category buries the other.
 */
export type AesLogSource = 'logz' | 'errorz' | 'rejectz';

/** Map the console's source name to the control-plane log path. */
const SOURCE_PATH: Record<AesLogSource, string> = {
  logz: '/logs',
  errorz: '/logs/errors',
  rejectz: '/logs/rejects',
};

export interface AesLogsFilters {
  /** Which view to read; defaults to the full `logz` tail. */
  source?: AesLogSource;
  /** Case-insensitive event-name prefix (e.g. `live.keyfile.upstream_error`). */
  event?: string;
  /** Case-insensitive substring on the raw line (e.g. a stream name `vtv6plus`). */
  q?: string;
  /** Max events to return (server clamps to its cap). */
  limit?: number;
  /** Poll interval ms; 0/undefined → no auto-refresh. */
  refreshMs?: number;
}

const EMPTY: AesLogsResponse = { events: [], count: 0, capacity: 0, total: 0 };

export function useAesLogs(serviceId: string | undefined, filters: AesLogsFilters = {}) {
  const { source = 'logz', event, q, limit = 200, refreshMs } = filters;
  return useQuery<AesLogsResponse>({
    queryKey: aesKeys.logs(serviceId ?? '', { source, event, q, limit }),
    enabled: !!serviceId,
    refetchInterval: refreshMs && refreshMs > 0 ? refreshMs : false,
    staleTime: 2_000,
    retry: false,
    queryFn: async () => {
      const params: Record<string, string | number> = { limit };
      if (event) params.event = event;
      if (q) params.q = q;
      const res = await controlApi.get<AesLogsResponse>(SOURCE_PATH[source], { params });
      return res.data ?? EMPTY;
    },
  });
}
